import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Check, Zap, Target, Activity } from 'lucide-react';
import MiraLogo from '../ui/MiraLogo';
import './LandingPageV2.css';

/* --------------------------------------------------------------------------
   ESTRUTURA — "Anatomy of a High-Converting Landing Page"
   Hero → Prova social → Benefícios (3 linhas alternadas, CTA repetido) →
   Depoimentos → Preço → FAQ → CTA final → Footer mínimo.
   Escaneabilidade: eyebrow + headline forte + microcopy junto de cada CTA.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   DADOS - FAQ (objection handling)
   -------------------------------------------------------------------------- */
const FAQS = [
  {
    q: 'O Game Pro Hub serve para o meu FPS mobile?',
    a: 'Sim. A mecânica de controle de recuo, rastreamento de alvos (tracking), flick e calibração de giroscópio segue leis físicas universais nos celulares. A Mi adapta os cálculos e rotinas perfeitamente para Free Fire, PUBG Mobile, COD Mobile, Blood Strike, Warzone Mobile e Standoff 2.'
  },
  {
    q: 'Por que eu preciso da Mi se já treino todos os dias?',
    a: 'Treinar errado apenas fixa maus hábitos na sua memória muscular. Jogar horas seguidas sem entender se sua sensibilidade está alta ou baixa causa efeito platô. A Mi identifica a falha técnica em minutos e entrega o ajuste matemático exato, economizando semanas de frustração.'
  },
  {
    q: 'Como funciona o diagnóstico se eu não preciso enviar vídeo?',
    a: 'A IA utiliza um algoritmo de análise comportamental de mira baseado em situações de combate: como sua mira se comporta nos primeiros tiros, em spray longo, ou no acompanhamento de alvos móveis. A partir dessas variáveis, ela isola o erro (ex: jitter horizontal ou descompasso no giroscópio) com precisão analítica.'
  },
  {
    q: 'A calibração da Mi é garantida ou é só um palpite de inteligência artificial?',
    a: 'Diferente de chatbots genéricos, a Mi opera conectada a um motor de cálculo de sensibilidade e balística de FPS. Ela não dá palpites teóricos; ela calcula fatores de compensação física e de resposta de tela para gerar seu setup ideal.'
  },
  {
    q: 'Qual a diferença entre tentar evoluir sozinho e usar o Game Pro Hub?',
    a: 'Sozinho, você depende de copiar sensibilidade de pro players (que jogam em devices diferentes do seu) e tentar adivinhar o que está errando. Com o GPH, você tem um sistema de alto rendimento no seu bolso que mede, calcula e direciona cada treino para a sua deficiência técnica atual.'
  },
  {
    q: 'Posso testar antes de assinar o plano Pro?',
    a: 'Com certeza. O Plano Free dá acesso imediato para você testar a interface, conversar com a Mi e realizar diagnósticos iniciais sem gastar nada. Quando sentir o impacto e quiser desbloquear calibração avançada ilimitada e rotinas completas, basta migrar para o Pro a qualquer momento.'
  }
];

/* --------------------------------------------------------------------------
   DADOS - PROVA SOCIAL & MÉTRICAS
   -------------------------------------------------------------------------- */
const SUPPORTED_GAMES = [
  { name: 'PUBG Mobile', category: 'Battle Royale • Giro & Recuo Vertical', status: 'COMPENSAÇÃO BALÍSTICA 4X/6X' },
  { name: 'Free Fire', category: 'Fast Paced • Giroscópio & Touch', status: 'PUXADA CAPA & SUBIDA DPI' },
  { name: 'COD Mobile', category: 'Tactical MP & BR • 120 FPS Setup', status: 'SENSI ADS & CROSSHAIR' },
  { name: 'Blood Strike', category: 'Ultra Fast Movement • Tracking', status: 'TRACKING ALTA MOBILIDADE' },
  { name: 'Warzone Mobile', category: 'Next-Gen Engine • Balística Complexa', status: 'CONTROLE RECUO HORIZONTAL' },
  { name: 'Standoff 2', category: 'Tactical 5v5 • Bombeiro & Sniper', status: 'MICRO-PRECISÃO & TAP' },
  { name: 'Farlight 84', category: 'Hero Shooter • Mobilidade Vertical', status: 'MIRA EM ALVOS VERTICAIS' },
  { name: 'Delta Force', category: 'Tactical Warfare • Operações Especiais', status: 'MULTI-SCOPE & SUPRESSÃO' }
];

const IMPACT_METRICS = [
  {
    number: '1.480+',
    label: 'Jogadores Ativos Calibrados',
    desc: 'Competidores de Free Fire, PUBG e COD Mobile que abandonaram vídeos genéricos pela calibração individual da Mi.',
    badge: 'CRESCIMENTO DIÁRIO'
  },
  {
    number: '-34%',
    label: 'Redução de Tempo de Reação',
    desc: 'Média cronometrada em clipes de jogadas após 7 dias de treino tático individualizado com foco na falha mecânica.',
    badge: 'MÉTRICA COMPROVADA'
  },
  {
    number: '+4.2',
    label: 'Divisões de Elo Subidas no Mês',
    desc: 'Média de progressão em ranqueada de jogadores que travavam no Platina/Diamante antes de corrigir o giroscópio.',
    badge: 'RANQUEADA MOBILE'
  },
  {
    number: '98.6%',
    label: 'Precisão no Diagnóstico Técnico',
    desc: 'Índice de aprovação do ajuste prescrito pela Mi para eliminação imediata de jitter lateral e recuo vertical.',
    badge: 'PRECISÃO CIRÚRGICA'
  }
];

/* --------------------------------------------------------------------------
   HOOK — IntersectionObserver para animações de entrada
   -------------------------------------------------------------------------- */
function useReveal(threshold = 0.1) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const targets = el.querySelectorAll('.lp-reveal, .lp-stagger');
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}

/* --------------------------------------------------------------------------
   COMPONENTE PRINCIPAL
   -------------------------------------------------------------------------- */
export default function LandingPageV2() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('anual'); // 'mensal' ou 'anual'

  // Estados para simulação de conversa dinâmica no Hero
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTyping, setActiveTyping] = useState('');
  const [typingSender, setTypingSender] = useState('');

  const revealHero = useReveal();
  const revealProof = useReveal();
  const revealBenefits = useReveal();
  const revealTestimonials = useReveal();
  const revealAiComparison = useReveal();
  const revealPro = useReveal();
  const revealFaq = useReveal();
  const revealFinal = useReveal();

  const lastScrollY = useRef(0);

  // Hook Scroll para Minimizar e Esconder/Mostrar Header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);

      if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        setHeaderHidden(true);
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentScrollY;

      // Progresso de leitura (0–100): alimenta a barra premium do topo.
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      setScrollProgress(Math.min(100, Math.max(0, (currentScrollY / max) * 100)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Simulação de Digitação Dinâmica no Chat do Hero
  useEffect(() => {
    let isMounted = true;
    let timerId = null;

    const delay = (ms) => new Promise(res => { timerId = setTimeout(res, ms); });

    const typeText = async (text) => {
      let current = '';
      for (let i = 0; i < text.length; i++) {
        if (!isMounted) return;
        current += text[i];
        setActiveTyping(current);
        await new Promise(res => { timerId = setTimeout(res, 35); });
      }
    };

    const runSimulation = async () => {
      if (!isMounted) return;
      setChatMessages([]);
      setActiveTyping('');
      setTypingSender('');
      await delay(600);

      if (!isMounted) return;
      setTypingSender('user');
      await typeText('Minha mira sobe quando atiro.');
      await delay(500);
      if (!isMounted) return;
      setChatMessages(prev => [...prev, { sender: 'user', text: 'Minha mira sobe quando atiro.' }]);
      setActiveTyping('');
      setTypingSender('');
      await delay(600);

      if (!isMounted) return;
      setChatMessages(prev => [...prev, { sender: 'mi', text: 'Isso acontece mais em spray longo ou nos primeiros tiros?' }]);
      await delay(1200);

      if (!isMounted) return;
      setTypingSender('user');
      await typeText('Spray longo.');
      await delay(500);
      if (!isMounted) return;
      setChatMessages(prev => [...prev, { sender: 'user', text: 'Spray longo.' }]);
      setActiveTyping('');
      setTypingSender('');
      await delay(600);

      if (!isMounted) return;
      setChatMessages(prev => [...prev, { sender: 'mi', text: 'Diagnóstico provável: sensibilidade vertical alta para spray contínuo. Próximo passo: ajuste recomendado + rotina de treino de controle de spray.' }]);
    };

    runSimulation();

    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, []);

  // Desbloquear overflow
  useEffect(() => {
    const unlock = () => {
      const force = (el) => {
        if (!el) return;
        el.style.setProperty('overflow-y', 'auto', 'important');
        el.style.setProperty('overflow-x', 'hidden', 'important');
        el.style.setProperty('height', 'auto', 'important');
      };
      force(document.documentElement);
      force(document.body);

      ['#root', '.mi-root', '.mi-shell-content', '.app-main-layout', '.app-container'].forEach((sel) =>
        document.querySelectorAll(sel).forEach((el) => {
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', '100%', 'important');
        })
      );
    };

    unlock();
    const interval = setInterval(unlock, 300);

    return () => {
      clearInterval(interval);
      ['overflow-y', 'overflow-x', 'height'].forEach((p) => {
        document.documentElement.style.removeProperty(p);
        document.body.style.removeProperty(p);
      });
      ['#root', '.mi-root', '.mi-shell-content', '.app-main-layout', '.app-container'].forEach((sel) =>
        document.querySelectorAll(sel).forEach((el) => {
          el.style.removeProperty('overflow');
          el.style.removeProperty('height');
          el.style.removeProperty('min-height');
        })
      );
    };
  }, []);

  const toggleFaq = useCallback((idx) => setOpenFaq((prev) => (prev === idx ? null : idx)), []);

  return (
    <div className="gph-landing-v2">
      {/* Barra de progresso de leitura — acento premium fixo no topo */}
      <div className="lp-scroll-progress" aria-hidden="true">
        <span className="lp-scroll-progress__bar" style={{ transform: `scaleX(${scrollProgress / 100})` }} />
      </div>

      {/* ──────────────────────────────────────
          1. HEADER
      ────────────────────────────────────── */}
      <header className={`lp-header ${isScrolled ? 'is-scrolled' : ''} ${headerHidden ? 'lp-header--hidden' : ''}`}>
        <a href="/" className="lp-header__brand">
          <MiraLogo size={28} />
        </a>

        <ul className="lp-header__links">
          <li><a href="#beneficios">Benefícios</a></li>
          <li><a href="#depoimentos">Depoimentos</a></li>
          <li><a href="#pricing">Preço</a></li>
          <li><a href="#faq">FAQ</a></li>
        </ul>

        <div className="lp-header__actions">
          <a href="/login" className="lp-btn lp-btn--ghost lp-btn--sm">
            Entrar
          </a>
          <a href="/login?mode=signup" className="lp-btn lp-btn--primary lp-btn--sm">
            Começar
          </a>
        </div>
      </header>

      {/* ──────────────────────────────────────
          2. HERO (preservado — copy curta, demo real, CTA + microcopy)
      ────────────────────────────────────── */}
      <section className="lp-hero" ref={revealHero} aria-label="Game Pro Hub">
        <div className="lp-hero__bg" aria-hidden="true" />
        <div className="lp-hero__grid" aria-hidden="true" />
        <div className="lp-hero__vignette" aria-hidden="true" />

        <div className="lp-hero__content lp-container">
          <div className="lp-hero__copy">
            <h1 className="lp-hero__headline">
              Sua mira não<br />
              precisa de sorte.<br />
              Precisa de <em>diagnóstico.</em>
            </h1>

            <p className="lp-hero__sub">
              Conheça a <strong>Mi</strong>, a IA do Game Pro Hub especialista em FPS mobile. Ao criar sua conta, é com ela que você treina: você diz o que está acontecendo no jogo e a Mi entrega o próximo passo — ajuste de sensibilidade, cálculo de miras, treino específico ou análise de gameplay.
            </p>

            <div className="lp-hero__actions-wrapper">
              <div className="lp-hero__actions">
                <a href="/login?mode=signup" className="lp-btn lp-btn--primary" id="hero-cta-primary">
                  Começar grátis
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a href="#pricing" className="lp-btn lp-btn--secondary">
                  Ver o Pro
                </a>
              </div>
              <p className="lp-hero__microcopy">
                Free para testar, sem cartão de crédito. Pro a partir de R$&nbsp;24,16/mês.
              </p>
            </div>
          </div>

          {/* CHAT WIDGET DINÂMICO DE SIMULAÇÃO EM TEMPO REAL */}
          <aside className="lp-hero__chat" aria-label="Simulação de conversa com a Mi">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`chat-bubble visible chat-bubble--${msg.sender}`}>
                <span className="chat-author">{msg.sender === 'user' ? 'Você' : 'Mi'}</span>
                <p className="chat-text">{msg.text}</p>
              </div>
            ))}

            {typingSender && (
              <div className={`chat-bubble visible chat-bubble--${typingSender}`}>
                <span className="chat-author">{typingSender === 'user' ? 'Você' : 'Mi'}</span>
                <p className="chat-text">
                  {activeTyping}
                  <span className="chat-cursor" />
                </p>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* ──────────────────────────────────────
          3. PROVA SOCIAL — jogos suportados (calibrated cards deck)
      ────────────────────────────────────── */}
      <section className="lp-logos" ref={revealProof} aria-label="Jogos suportados">
        <div className="lp-container">
          <p className="lp-logos__title lp-reveal">A Mi é calibrada para os FPS mobile que você joga</p>
          <div className="lp-logos__grid lp-stagger">
            {SUPPORTED_GAMES.map((game) => (
              <div key={game.name} className="lp-game-card">
                <div className="lp-game-card__top">
                  <span className="lp-game-card__dot" aria-hidden="true" />
                  <span className="lp-game-card__status">{game.status}</span>
                </div>
                <h3 className="lp-game-card__name">{game.name}</h3>
                <span className="lp-game-card__cat">{game.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          4. BENEFÍCIOS — 3 linhas com conversas interativas da Mi
      ────────────────────────────────────── */}
      <section id="beneficios" className="lp-benefits" ref={revealBenefits}>
        <div className="lp-container">
          <div className="lp-benefits__header text-center">
            <h2 className="lp-pain__title lp-reveal">Do "não sei o que está errado"<br />para o próximo elo</h2>
            <p className="lp-pain__sub text-center lp-reveal lp-reveal--delay-1" style={{ marginInline: 'auto' }}>
              A Mi transforma a sua dor no jogo em três entregas concretas: diagnóstico cirúrgico, calibração balística e treino validado em vídeo.
            </p>
          </div>

          {/* Benefício 1 — Diagnóstico Causa Raiz */}
          <div className="lp-benefit">
            <div className="lp-benefit__copy lp-reveal lp-reveal--left">
              <span className="lp-benefit__eyebrow">Diagnóstico de causa raiz</span>
              <h3 className="lp-benefit__title">Chega de perder trocação por sensibilidade errada ou mira tremendo</h3>
              <p className="lp-benefit__desc">
                O maior erro de 90% dos jogadores é mexer na configuração no chute sem saber se o problema é o giroscópio, a aceleração de dedo ou o recuo vertical. Você descreve o que sentiu na ranqueada e a Mi isola a causa física exata do seu aparelho.
              </p>
              <ul className="lp-benefit__bullets lp-stagger">
                <li><Check size={15} aria-hidden="true" /> Identificação cirúrgica entre erro de mecânica (dedo) e erro de setup</li>
                <li><Check size={15} aria-hidden="true" /> Diagnóstico em menos de 1 minuto diretamente na conversa</li>
                <li><Check size={15} aria-hidden="true" /> Ajuste exato em pontos, sem adivinhação</li>
              </ul>
              <div className="lp-benefit__cta-row">
                <a href="/login?mode=signup" className="lp-btn lp-btn--primary">
                  Fazer meu diagnóstico grátis
                  <ArrowRight size={15} aria-hidden="true" />
                </a>
                <p className="lp-benefit__micro">Sem cartão de crédito • Resultado na hora.</p>
              </div>
            </div>

            <div className="lp-benefit__visual lp-reveal lp-reveal--right lp-reveal--delay-1">
              <aside className="lp-hero__chat" aria-label="Simulação de conversa com a Mi">
                <div className="chat-bubble visible chat-bubble--user">
                  <span className="chat-author">Você</span>
                  <p className="chat-text">"Mi, quando pego a M4 e abro a mira 3x, os primeiros tiros vão certo no peito, mas depois o spray começa a subir e pular pros lados."</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <p className="chat-text">"O salto vertical indica que o Giroscópio 3x está baixo para compensar a taxa de disparo da M4. Já o 'pulo pros lados' (jitter lateral) acontece porque você está tensionando o polegar para compensar o peso vertical."</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <p className="chat-text highlight-chat-val">"Aumente o Giroscópio 3x de 140 para 156 (+16 pts). Reduza a câmera ADS 3x em -4 pts para estabilizar o micro-tracking lateral."</p>
                </div>
              </aside>
            </div>
          </div>

          {/* Benefício 2 — Motor Balístico de Miras (flip no desktop) */}
          <div className="lp-benefit lp-benefit--flip">
            <div className="lp-benefit__copy lp-reveal lp-reveal--right">
              <span className="lp-benefit__eyebrow">Motor de cálculo balístico</span>
              <h3 className="lp-benefit__title">Uma única mira calibrada gera todas as outras perfeitas</h3>
              <p className="lp-benefit__desc">
                Copiar código de pro player estraga sua gameplay porque a densidade de tela, o polling rate e o peso do giroscópio do seu celular são únicos. A Mi usa a sua Red Dot perfeita como âncora matemática para calcular exatamente quanto deve ser a sua 2x, 3x, 4x e Sniper.
              </p>
              <ul className="lp-benefit__bullets lp-stagger">
                <li><Check size={15} aria-hidden="true" /> Fórmula proprietária adaptada ao FOV (Campo de Visão) do seu jogo</li>
                <li><Check size={15} aria-hidden="true" /> Sincronização milimétrica entre mira aberta (ADS) e giroscópio</li>
                <li><Check size={15} aria-hidden="true" /> Memória muscular preservada: a sensação de velocidade fica idêntica</li>
              </ul>
              <div className="lp-benefit__cta-row">
                <a href="/login?mode=signup" className="lp-btn lp-btn--primary">
                  Calcular minhas miras
                  <ArrowRight size={15} aria-hidden="true" />
                </a>
                <p className="lp-benefit__micro">Teste grátis no navegador.</p>
              </div>
            </div>

            <div className="lp-benefit__visual lp-reveal lp-reveal--left lp-reveal--delay-1">
              <aside className="lp-hero__chat" aria-label="Simulação de conversa com a Mi">
                <div className="chat-bubble visible chat-bubble--user">
                  <span className="chat-author">Você</span>
                  <p className="chat-text">"Minha Red Dot está perfeita no 185 (giro) e 120 (toque). Mas quando puxo a mira 4x ou a Sniper, parece muito pesada e perco o tracking de longe."</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <p className="chat-text">"Aplicando matriz de FOV angular do seu celular. A física do seu aparelho reduz a taxa angular em miras de alto zoom por padrão."</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <div className="chat-text highlight-chat-val" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Red Dot (Âncora): Giro 185 | Toque 120</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Mira 2x: Giro 158 | Toque 102</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Mira 3x: Giro 134 | Toque 88</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Mira 4x: Giro 112 | Toque 74</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Sniper 6x/8x: Giro 84 | Toque 52</span>
                  </div>
                </div>
              </aside>
            </div>
          </div>

          {/* Benefício 3 — Rotina e Validação de Clipe */}
          <div className="lp-benefit">
            <div className="lp-benefit__copy lp-reveal lp-reveal--left">
              <span className="lp-benefit__eyebrow">Treino tático &amp; validação</span>
              <h3 className="lp-benefit__title">Não apenas te damos o número: provamos sua evolução em vídeo</h3>
              <p className="lp-benefit__desc">
                Ajustar a sensibilidade é metade da vitória; blindar sua memória muscular é a outra metade. A Mi monta treinos curtos de 12 minutos focados na sua fraqueza e analisa seus clipes de jogadas para cronometrar milissegundos do seu tempo de reação e precisão no spray.
              </p>
              <ul className="lp-benefit__bullets lp-stagger">
                <li><Check size={15} aria-hidden="true" /> Treinos curtos de 12 minutos focados no seu erro diagnosticado</li>
                <li><Check size={15} aria-hidden="true" /> Análise frame a frame de clipes de jogadas reais enviadas por você</li>
                <li><Check size={15} aria-hidden="true" /> Relatório de performance: tempo de reação, crosshair placement e recoil</li>
              </ul>
              <div className="lp-benefit__cta-row">
                <a href="/login?mode=signup" className="lp-btn lp-btn--primary">
                  Começar a treinar grátis
                  <ArrowRight size={15} aria-hidden="true" />
                </a>
                <p className="lp-benefit__micro">Acesso imediato, sem instalar nada.</p>
              </div>
            </div>

            <div className="lp-benefit__visual lp-reveal lp-reveal--right lp-reveal--delay-1">
              <aside className="lp-hero__chat" aria-label="Simulação de conversa com a Mi">
                <div className="chat-bubble visible chat-bubble--user">
                  <span className="chat-author">Você</span>
                  <p className="chat-text">"Ajustei a mira e treinei 3 dias como você mandou. Olha esse clipe que gravei no 1v2 na ranqueada hoje [Vídeo anexo: clipe_clutch_1v2.mp4]."</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <p className="chat-text">"Análise de Vídeo Concluída! Evolução drástica detectada em 3 pilares de combate após o seu novo setup:"</p>
                </div>
                <div className="chat-bubble visible chat-bubble--mi">
                  <span className="chat-author">Mi</span>
                  <div className="chat-text highlight-chat-val" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Zap size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Tempo de reação ao abrir mira: 290ms → 180ms (-38%)</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Target size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Estabilidade de Recuo Vertical: 98.4% no centro do alvo</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: 'var(--lp-accent)', flexShrink: 0 }} /> Posicionamento de Mira (Crosshair Placement): 94% na cabeça</span>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          5. PROVA SOCIAL QUANTITATIVA — Métricas de Impacto
      ────────────────────────────────────── */}
      <section id="depoimentos" className="lp-testimonials" ref={revealTestimonials}>
        <div className="lp-container">
          <div className="lp-testimonials__header text-center">
            <h2 className="lp-pain__title lp-reveal">Quem treina com a Mi sente na ranqueada</h2>
            <p className="lp-pain__sub text-center lp-reveal lp-reveal--delay-1" style={{ marginInline: 'auto' }}>
              Números monitorados diretamente da progressão de jogadores nos principais FPS competitivos.
            </p>
          </div>

          <div className="lp-metrics__grid lp-stagger lp-stagger--zoom">
            {IMPACT_METRICS.map((item) => (
              <div key={item.label} className="lp-metric-card">
                <span className="lp-metric-card__badge">{item.badge}</span>
                <span className="lp-metric-card__number">{item.number}</span>
                <h3 className="lp-metric-card__label">{item.label}</h3>
                <p className="lp-metric-card__desc">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="lp-metrics__banner lp-reveal">
            <div className="lp-metrics__banner-content">
              <span className="lp-metrics__banner-text">
                O próximo diagnóstico pode ser o ponto de virada da sua ranqueada.
              </span>
              <a href="/login?mode=signup" className="lp-btn lp-btn--primary lp-btn--sm">
                Junte-se a 1.480+ jogadores
                <ArrowRight size={14} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          5.5 DUELO AI — Uma IA genérica vs A Mi
      ────────────────────────────────────── */}
      <section className="lp-ai-vs-section" ref={revealAiComparison}>
        <div className="lp-container">
          <div className="lp-ai-vs-header text-center">
            <span className="lp-ai-vs-eyebrow lp-reveal">ESPECIALIZADO EM FPS MOBILE</span>
            <h2 className="lp-ai-vs-title lp-reveal lp-reveal--delay-1" style={{ marginInline: 'auto' }}>
              Uma IA genérica te dá uma resposta.<br />A Mi monta seu próximo passo.
            </h2>
            <p className="lp-ai-vs-sub lp-reveal lp-reveal--delay-2" style={{ marginInline: 'auto' }}>
              Criado para jogadores de FPS Mobile, o Game Pro Hub não tenta ser um chat para qualquer assunto. Ele entende seu jogo, sua mira, seu erro e transforma isso em treino, ajuste e evolução prática.
            </p>
          </div>

          <div className="lp-ai-vs-grid lp-reveal lp-reveal--delay-3">
            {/* COLUMN LEFT - GENERIC AI */}
            <div className="ai-vs-card generic-ai">
              <div className="ai-vs-card__header">
                <span className="ai-vs-card__eyebrow">CHATBOT TRADICIONAL</span>
                <h3 className="ai-vs-card__title">IA Genérica</h3>
              </div>
              <p className="ai-vs-card__intro">
                Boa para explicar conceitos teóricos. Limitada para transformar seu erro dentro do jogo em uma ação prática.
              </p>
              <div className="ai-vs-card__chat-wrap">
                <aside className="lp-hero__chat lp-hero__chat--generic" aria-label="Simulação com IA Genérica">
                  <div className="chat-bubble visible chat-bubble--user">
                    <span className="chat-author">Você</span>
                    <p className="chat-text">"Como melhorar minha mira de M4 no PUBG?"</p>
                  </div>
                  <div className="chat-bubble visible chat-bubble--generic">
                    <span className="chat-author">IA Genérica</span>
                    <p className="chat-text">"Para melhorar sua mira, procure treinar em mapas de treino, use acessórios como compensador e empunhadura vertical, puxe o dedo para baixo e ajuste a sensibilidade até ficar confortável."</p>
                  </div>
                </aside>
              </div>
              <p className="ai-vs-card__note">Respostas teóricas, vagas e sem cálculo de sensibilidade balística para o seu aparelho.</p>
            </div>

            {/* COLUMN RIGHT - GPH (A Mi) */}
            <div className="ai-vs-card gph-ai">
              <div className="ai-vs-card__header">
                <span className="ai-vs-card__eyebrow accent-text">CENTRAL OPERACIONAL</span>
                <h3 className="ai-vs-card__title">A Mi • IA Especializada</h3>
              </div>
              <p className="ai-vs-card__intro">
                Criada para executar: diagnosticar sensibilidade, calcular mira, gerar treinos táticos e guiar sua evolução técnica.
              </p>
              <div className="ai-vs-card__chat-wrap">
                <aside className="lp-hero__chat" aria-label="Simulação com a Mi">
                  <div className="chat-bubble visible chat-bubble--user">
                    <span className="chat-author">Você</span>
                    <p className="chat-text">"Minha mira de M4 no PUBG sobe nos 5 primeiros tiros e treme na lateral depois."</p>
                  </div>
                  <div className="chat-bubble visible chat-bubble--mi">
                    <span className="chat-author">Mi</span>
                    <p className="chat-text">"O salto inicial indica Giroscópio Red Dot baixo em relação à taxa de disparo. O tremor lateral indica excesso de tensão no polegar ao compensar o peso."</p>
                  </div>
                  <div className="chat-bubble visible chat-bubble--mi">
                    <span className="chat-author">Mi</span>
                    <p className="chat-text highlight-chat-val">"Ajuste aplicado: Giroscópio +14 pts para eliminar o salto de entrada. Câmera lateral -6 pts para estabilizar o micro-jitter. Treino tático de 12 min liberado."</p>
                  </div>
                </aside>
              </div>
              <p className="ai-vs-card__note">Diagnóstico cirúrgico com cálculo balístico real no seu FPS Mobile.</p>
            </div>
          </div>

          <div className="lp-ai-vs-footer text-center lp-reveal lp-reveal--delay-3">
            <p className="lp-ai-vs-reinforce">
              Por <strong>R$ 24,16/mês</strong> no plano anual, o GPH entrega uma estrutura que uma IA genérica não foi criada para oferecer: contexto, calibração, treino e evolução contínua.
            </p>
            <p className="lp-ai-vs-microcopy">Você não paga por conversa. Você paga por evolução.</p>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          6. PREÇO (dois planos, CTA com microcopy de confiança)
      ────────────────────────────────────── */}
      <section id="pricing" className="lp-pro-sec" ref={revealPro}>
        <div className="lp-container">
          <div className="lp-pro-sec__header text-center">
            <h2 className="lp-pro-sec__title lp-reveal" style={{ marginInline: 'auto' }}>
              Escolha sua rota de evolução
            </h2>
            <p className="lp-pro-sec__sub lp-reveal lp-reveal--delay-1" style={{ marginInline: 'auto', marginTop: '16px' }}>
              Crie sua conta grátis no Game Pro Hub e comece a treinar com a Mi — ou libere todo o potencial dela com o plano Pro.
            </p>
          </div>

          <div className="lp-pricing-grid lp-stagger lp-stagger--rise">

            {/* PLANO FREE */}
            <div className="pricing-card">
              <span className="pricing-card__badge">INICIANTE</span>
              <h3 className="pricing-card__title">FREE</h3>
              <p className="pricing-card__desc">Converse com a Mi e teste o diagnóstico de mira em ambiente real.</p>

              <div className="pricing-card__price-wrapper">
                <div className="pricing-card__price">R$&nbsp;0<span>/mês</span></div>
                <p className="pricing-card__note">Sem cartão de crédito necessário.</p>
              </div>

              <ul className="pricing-card__bullets">
                <li><Check size={14} className="green-text" /> 3 diagnósticos guiados por semana</li>
                <li><Check size={14} className="green-text" /> Calculadora de sensibilidade básica</li>
                <li><Check size={14} className="green-text" /> Recomendações de treino padrão</li>
              </ul>

              <a href="/login?mode=signup" className="lp-btn lp-btn--secondary">
                Começar grátis
              </a>
            </div>

            {/* PLANO PRO */}
            <div className="pricing-card highlight">
              <span className="pricing-card__badge-selection">MAIS INTELIGENTE</span>
              <h3 className="pricing-card__title">PRO</h3>
              <p className="pricing-card__desc">A Mi sem limites: motor de cálculo completo e rotinas avançadas de evolução técnica.</p>

              <div className="lp-pro-sec__toggle" style={{ margin: '14px 0' }}>
                <button
                  onClick={() => setBillingPeriod('mensal')}
                  className={`toggle-btn ${billingPeriod === 'mensal' ? 'active' : ''}`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setBillingPeriod('anual')}
                  className={`toggle-btn ${billingPeriod === 'anual' ? 'active' : ''}`}
                >
                  Anual
                </button>
              </div>

              <div className="pricing-card__price-wrapper">
                {billingPeriod === 'mensal' ? (
                  <>
                    <div className="pricing-card__price">R$&nbsp;29,99<span>/mês</span></div>
                    <p className="pricing-card__note">Cobrado mensalmente. Cancele quando quiser.</p>
                  </>
                ) : (
                  <>
                    <div className="pricing-card__price">R$&nbsp;24,16<span>/mês</span></div>
                    <span className="pricing-card__saving-badge">Economize 19% no plano anual</span>
                    <p className="pricing-card__note">R$&nbsp;289,90 cobrados anualmente (12 meses de acesso Pro).</p>
                  </>
                )}
              </div>

              <ul className="pricing-card__bullets">
                <li><Check size={14} className="green-text" /> Diagnósticos técnicos ilimitados com a Mi</li>
                <li><Check size={14} className="green-text" /> Calibração avançada de sensibilidade (ADS, Giro, Câmera)</li>
                <li><Check size={14} className="green-text" /> Gerador de rotinas táticas personalizadas</li>
                <li><Check size={14} className="green-text" /> Módulo de análise balística de gameplay</li>
                <li><Check size={14} className="green-text" /> Acesso VIP ao grupo de evolução de jogadores</li>
              </ul>

              <a href="/login?redirect=checkout" className="lp-btn lp-btn--primary" id="pro-cta">
                Assinar Pro agora
                <ArrowRight size={16} aria-hidden="true" />
              </a>
              <p className="pricing-card__trust">Acesso imediato · Cancele quando quiser · Pagamento seguro</p>
            </div>

          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          7. FAQ (objection handling)
      ────────────────────────────────────── */}
      <section id="faq" className="lp-faq" ref={revealFaq}>
        <div className="lp-container--narrow">
          <div className="lp-faq__header">
            <h2 className="lp-faq__title lp-reveal">Perguntas frequentes</h2>
          </div>

          <div className="lp-faq__list lp-reveal lp-reveal--delay-1">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={idx} className={`lp-faq-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="lp-faq-question"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                    id={`faq-btn-${idx}`}
                    aria-controls={`faq-body-${idx}`}
                  >
                    <span>{faq.q}</span>
                    <span className="lp-faq-question__icon" aria-hidden="true" />
                  </button>

                  <div
                    className="lp-faq-body"
                    id={`faq-body-${idx}`}
                    role="region"
                    aria-labelledby={`faq-btn-${idx}`}
                    aria-hidden={!isOpen}
                  >
                    <div className="lp-faq-body__inner">
                      <p className="lp-faq-answer">{faq.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────
          8. CTA FINAL — Minimalista, Leve e Alta Conversão (A Última Cartada)
      ────────────────────────────────────── */}
      <section className="lp-final" ref={revealFinal}>
        <div className="lp-container">
          <div className="lp-final__box-minimal lp-reveal">
            <span className="lp-final__overline">PRONTO PARA SUBIR DE ELO?</span>
            <h2 className="lp-final__headline">Sua sensibilidade perfeita está a menos de 2 minutos de distância.</h2>
            <p className="lp-final__description">
              Sem cartão de crédito. Sem precisar instalar nada no celular. Você acessa pelo navegador, descreve sua mira para a Mi e sai para o jogo calibrado.
            </p>

            <div className="lp-final__action-area">
              <a href="/login?mode=signup" className="lp-btn lp-btn--primary lp-btn--lg" id="final-cta">
                Criar minha conta grátis
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>

            <div className="lp-final__trust-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={14} style={{ color: 'var(--lp-accent)' }} /> 100% Grátis para testar</span>
              <span style={{ color: 'var(--lp-border-m)' }}>•</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={14} style={{ color: 'var(--lp-accent)' }} /> Calibração imediata para seu celular</span>
              <span style={{ color: 'var(--lp-border-m)' }}>•</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={14} style={{ color: 'var(--lp-accent)' }} /> Sem risco de banimento</span>
            </div>
          </div>
        </div>
      </section>


      {/* ──────────────────────────────────────
          9. FOOTER — apenas os links necessários
      ────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer__inner">
          <MiraLogo size={26} />
          <ul className="lp-footer__links">
            <li><a href="/terms">Termos</a></li>
            <li><a href="/privacy">Privacidade</a></li>
          </ul>
        </div>
        <div className="lp-container">
          <p className="lp-footer__copy">
            &copy; {new Date().getFullYear()} Game Pro Hub. Todos os direitos reservados.<br />
            Este sistema é uma plataforma independente e não possui vínculo com desenvolvedoras de jogos oficiais.
          </p>
        </div>
      </footer>
    </div>
  );
}
